import React, { useState } from "react";
import { ChevronDown, ChevronRight, FileText } from "lucide-react";
import { formatOrderDateTime } from "../../lib/formatDate";
import { api } from "../../lib/api";

// =====================================================================
// OrdersPanel — expandable rows revealing per-line-item project briefs.
// Each order row toggles a details panel with: customer notes + every
// cart line's meta (esp. meta.project_brief captured by PackageBuilder
// Step 3). Zero backend changes — everything already flows through
// CartItem.meta -> OrderModel.items.
// =====================================================================
export const OrdersPanel = ({ orders, onMarkSeen }) => {
  const [expandedId, setExpandedId] = useState(null);
  const toggle = (id) => {
    const opening = expandedId !== id;
    setExpandedId(expandedId === id ? null : id);
    // On EXPAND (not collapse), stamp admin_seen_at server-side. Uses
    // the server clock — the compare-and-set in the endpoint is
    // idempotent so repeat calls are safe.
    if (opening) {
      const target = orders.find((o) => o.id === id);
      if (target && !target.admin_seen_at) {
        api.post(`/orders/${id}/mark-seen`).catch(() => {
          /* non-fatal — server clock is source of truth; the badge will
             correct itself on next dashboard refresh. */
        });
        onMarkSeen && onMarkSeen(id);
      }
    }
  };
  return (
    <div data-testid="admin-orders-panel" className="ur-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#141414] text-[#A3A3A3]">
          <tr>
            <th className="w-10 p-4"></th>
            {["Order", "Customer", "Email", "Total", "Payment", "Order Status", "Date"].map((h) => (
              <th key={h} className="text-left p-4 font-semibold tracking-wider text-xs uppercase">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            // P0 — orders now store canonical `lines`; older records
            // may still expose `items`. Read both, prefer new.
            const lines = Array.isArray(o.lines) && o.lines.length ? o.lines : (o.items || []);
            const briefCount = lines.filter((it) => it?.meta?.project_brief).length;
            const isOpen = expandedId === o.id;
            const paymentStatus = o.payment_status || (o.status === "paid" ? "paid" : "pending");
            const isTest = paymentStatus === "test_mock";
            const paymentTone = isTest
              ? "bg-amber-500/15 border-amber-500/50 text-amber-300"
              : paymentStatus === "paid"
                ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300"
                : paymentStatus === "failed"
                  ? "bg-red-500/15 border-red-500/50 text-red-300"
                  : "bg-[#1A1A1A] border-[#262626] text-[#A3A3A3]";
            const paymentLabel = {
              pending: "Awaiting payment",
              test_mock: "TEST / MOCK — NO PAYMENT",
              authorized: "Authorized",
              paid: "Paid",
              failed: "Payment failed",
              refunded: "Refunded",
            }[paymentStatus] || paymentStatus;
            return (
              <React.Fragment key={o.id}>
                <tr
                  className={`border-t border-[#1F1F1F] cursor-pointer hover:bg-[#141414] ${isTest ? "bg-amber-500/[0.03]" : ""}`}
                  onClick={() => toggle(o.id)}
                  data-testid={`admin-order-row-${o.id}`}
                >
                  <td className="p-4">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-[#FF6600]" /> : <ChevronRight className="w-4 h-4 text-[#A3A3A3]" />}
                  </td>
                  <td className="p-4 font-mono text-xs">
                    {!o.admin_seen_at && (
                      <span
                        data-testid={`admin-order-new-${o.id}`}
                        className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#FF6600] text-white"
                      >NEW</span>
                    )}
                    {o.id}
                    {briefCount > 0 && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#FF6600]/12 border border-[#FF6600]/40 text-[#FF6600]"
                        title={`${briefCount} project brief(s) attached`}
                      >
                        <FileText className="w-3 h-3" /> Brief
                      </span>
                    )}
                  </td>
                  <td className="p-4">{o.customer_name}</td>
                  <td className="p-4 text-[#A3A3A3]">{o.customer_email}</td>
                  <td className="p-4 font-semibold">AWG {o.subtotal}</td>
                  <td className="p-4">
                    <span
                      className={`px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold border ${paymentTone}`}
                      data-testid={`admin-order-payment-${o.id}`}
                    >{paymentLabel}</span>
                    {/* Email delivery indicator — surfaces when either
                        channel's `_sent_at` is empty on an order whose
                        payment lifecycle already fired the dispatcher
                        (test_mock or paid). Clicking the retry pill
                        calls /resend-notifications which only re-fires
                        the missing channels. */}
                    {(paymentStatus === "test_mock" || paymentStatus === "paid") &&
                      (!o.customer_email_sent_at || !o.admin_email_sent_at) && (
                        <button
                          data-testid={`admin-order-email-retry-${o.id}`}
                          title={`Customer: ${o.customer_email_sent_at ? "sent" : "pending"} · Admin: ${o.admin_email_sent_at ? "sent" : "pending"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            api.post(`/orders/${o.id}/resend-notifications`).catch(() => {
                              /* non-fatal — backend logs the outcome */
                            });
                          }}
                          className="ml-2 px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold border border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                        >
                          Email Pending · Retry
                        </button>
                      )}
                  </td>
                  <td className="p-4"><span className="px-2 py-1 rounded-full text-xs bg-[#1A1A1A] border border-[#262626]">{o.status || "received"}</span></td>
                  <td
                    className="p-4 text-[#A3A3A3] text-xs"
                    data-testid={`admin-order-created-${o.id}`}
                    title={o.created_at || ""}
                  >{formatOrderDateTime(o.created_at)}</td>
                </tr>
                {isOpen && (
                  <tr className="border-t border-[#1F1F1F] bg-[#0A0A0A]">
                    <td colSpan={8} className="p-6">
                      <OrderDetails order={o} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {orders.length === 0 && (
            <tr><td colSpan={8} className="p-10 text-center text-[#A3A3A3]">No orders yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const OrderDetails = ({ order }) => {
  // P0 — canonical `lines` preferred over legacy `items`.
  const lines = Array.isArray(order.lines) && order.lines.length ? order.lines : (order.items || []);
  return (
  <div data-testid={`admin-order-details-${order.id}`} className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3]">Customer</div>
      <div className="mt-2 space-y-1 text-sm">
        <div><span className="text-[#A3A3A3]">Name:</span> {order.customer_name}</div>
        <div><span className="text-[#A3A3A3]">Email:</span> {order.customer_email}</div>
        {order.customer_phone && <div><span className="text-[#A3A3A3]">Phone:</span> {order.customer_phone}</div>}
        {order.notes && (
          <div className="mt-3">
            <div className="text-[#A3A3A3] text-xs uppercase tracking-widest">Order Notes</div>
            <div className="mt-1 text-sm whitespace-pre-wrap">{order.notes}</div>
          </div>
        )}
      </div>
    </div>
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3]">Line Items</div>
      <div className="mt-2 space-y-3">
        {lines.map((it, idx) => {
          const displayName = it.display_name_snapshot || it.name || "";
          const unit = it.unit_price_snapshot ?? it.price ?? 0;
          const qty = it.quantity ?? it.qty ?? 1;
          const lineTotal = it.line_total ?? (unit * qty);
          const kindLabel = it.kind || "service";
          const key = it.item_id || it.item_code || `${idx}`;
          const lineRecurring = Number(it.recurring_price_snapshot || 0);
          const lineRecurringLabel = (it.recurring_label_snapshot || "").trim();
          return (
          <div key={key} data-testid={`admin-order-line-${it.item_code || key}`} className="rounded-lg border border-[#262626] bg-[#141414] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-sm">{displayName}</div>
                <div className="text-[11px] text-[#7A7A7A] mt-0.5">
                  {kindLabel} · qty {qty}
                  {it.item_code ? <> · <span className="font-mono text-[#666]">{it.item_code}</span></> : null}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-bold text-sm">AWG {lineTotal}</div>
                {qty > 1 && (
                  <div className="text-[10px] text-[#7A7A7A] mt-0.5">AWG {unit} × {qty}</div>
                )}
                {lineRecurring > 0 && lineRecurringLabel && (
                  <div
                    data-testid={`admin-order-line-recurring-${it.item_code || key}`}
                    className="text-[10px] text-[#FF6600] mt-1 whitespace-nowrap"
                  >
                    + AWG {lineRecurring}/{lineRecurringLabel}
                  </div>
                )}
              </div>
            </div>
            <ProductionMetaBlock it={it} />
            {it.meta?.project_brief && <ProjectBriefBlock brief={it.meta.project_brief} />}
          </div>
          );
        })}
      </div>
      {Number(order.recurring_subtotal || 0) > 0 && (order.recurring_label || "").trim() && (
        <div
          data-testid={`admin-order-recurring-${order.id}`}
          className="mt-4 rounded-lg border border-[#FF6600]/40 bg-[#FF6600]/5 px-4 py-3"
        >
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF6600]">
            Ongoing Recurring Commitment
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <span className="text-sm text-[#D1D1D1]">
              Website Care (recorded on order — collect separately)
            </span>
            <span className="font-display text-lg font-bold text-white whitespace-nowrap">
              AWG {order.recurring_subtotal}/{order.recurring_label}
            </span>
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

// P0 — Structured production data pulled from the canonical line's
// selected_options + meta. Rendered as compact key/value chips so admin
// production sees exactly what was configured without a raw JSON dump.
const ProductionMetaBlock = ({ it }) => {
  const opts = it.selected_options || {};
  const meta = it.meta || {};
  const base = opts.main_artwork_base_format || meta.main_artwork_base_format;
  const baseDims = base?.dimensions
    || (base?.width && base?.height && base?.unit
      ? `${base.width} × ${base.height} ${base.unit}`
      : null);
  const rawFormats = Array.isArray(opts.selected_formats)
    ? opts.selected_formats
    : (Array.isArray(meta.selected_formats) ? meta.selected_formats : []);
  const formats = rawFormats.filter(Boolean);
  const bizQty = meta.quantity ?? null;
  const brandingPath = meta.branding_service_path || "";
  const brandingEntry = meta.branding_entry_path || "";
  const existingLogo = meta.existing_logo_upload;
  const addonDetails = Array.isArray(meta.addon_details) ? meta.addon_details : [];
  const structuredAddons = Array.isArray(it.selected_addons) ? it.selected_addons : [];
  // Workstation-specific fields written by SetupConfiguratorPage.
  const isLaptop = it.kind === "laptop";
  const platform = meta.platform_display || meta.platform || "";
  const tierName = meta.tier_display || meta.tier || "";
  const currentModel = meta.current_model || "";
  const laptopAddons = Array.isArray(meta.addons) ? meta.addons.filter(Boolean) : [];
  const contact = meta.contact || null;
  const hasAny = base?.label || formats.length > 0 || bizQty || brandingPath || existingLogo
    || addonDetails.length > 0 || structuredAddons.length > 0
    || (isLaptop && (platform || tierName || laptopAddons.length || currentModel || contact));
  if (!hasAny) return null;
  return (
    <div data-testid="admin-production-data" className="mt-3 pt-3 border-t border-[#262626] space-y-2">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#4CB782]">Production Data</div>
      {base?.label && (
        <MetaRow testid="admin-meta-main-artwork-format" k="Main Artwork Format" v={<>
          <span className="text-white">{base.label.includes(" — ") ? base.label.split(" — ")[0] : base.label}</span>
          {baseDims && <span className="text-[#7A7A7A] ml-2 text-[11px]">{baseDims}</span>}
        </>} />
      )}
      {formats.length > 0 && (
        <MetaRow testid="admin-meta-additional-formats" k={`Additional Formats (${formats.length})`} v={<div className="space-y-0.5">
          {formats.map((f, i) => {
            const label = typeof f === "string" ? f : (f.label || f.id || "");
            const dims = typeof f === "object"
              ? (f.dimensions || (f.width && f.height && f.unit ? `${f.width} × ${f.height} ${f.unit}` : ""))
              : "";
            return (
              <div key={i} className="text-[11px]">
                <span className="text-white">{label.split(" — ")[0]}</span>
                {dims && <span className="text-[#7A7A7A] ml-2">{dims}</span>}
              </div>
            );
          })}
        </div>} />
      )}
      {bizQty != null && bizQty !== 1 && (
        <MetaRow testid="admin-meta-quantity" k="Quantity" v={<span className="text-white font-mono">{bizQty}</span>} />
      )}
      {(brandingEntry || brandingPath) && (
        <MetaRow k="Branding Path" v={<span className="text-white font-mono text-[11px]">{brandingEntry ? `${brandingEntry} · ` : ""}{brandingPath}</span>} />
      )}
      {existingLogo?.url && (
        <MetaRow testid="admin-meta-existing-logo" k="Existing Logo" v={<a
          href={(process.env.REACT_APP_BACKEND_URL || "") + existingLogo.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#FF6600] hover:underline text-[11px] truncate"
        >{existingLogo.filename || existingLogo.url}</a>} />
      )}
      {addonDetails.length > 0 && (
        <MetaRow testid="admin-meta-addons" k={`Add-ons (${addonDetails.length})`} v={<div className="space-y-0.5">
          {addonDetails.map((a, i) => (
            <div key={i} className="text-[11px]">
              <span className="text-white">{a.name}</span>
              {a.quantity > 1 && <span className="text-[#7A7A7A] ml-2">× {a.quantity}</span>}
              {a.price != null && <span className="text-[#7A7A7A] ml-2">AWG {a.price}</span>}
            </div>
          ))}
        </div>} />
      )}
      {isLaptop && platform && (
        <MetaRow testid="admin-meta-platform" k="Platform" v={<span className="text-white">{platform}</span>} />
      )}
      {isLaptop && tierName && (
        <MetaRow testid="admin-meta-tier" k="Tier" v={<span className="text-white">{tierName}</span>} />
      )}
      {isLaptop && currentModel && (
        <MetaRow testid="admin-meta-current-model" k="Current Model" v={<span className="text-white text-[11px]">{currentModel}</span>} />
      )}
      {isLaptop && laptopAddons.length > 0 && (
        <MetaRow testid="admin-meta-laptop-addons" k={`Add-ons (${laptopAddons.length})`} v={<div className="space-y-0.5">
          {laptopAddons.map((a, i) => (
            <div key={i} className="text-[11px] text-white">{a}</div>
          ))}
        </div>} />
      )}
      {isLaptop && contact && (
        <MetaRow testid="admin-meta-contact" k="Contact" v={<div className="text-[11px] text-white whitespace-pre-wrap">
          {[contact.name, contact.email, contact.phone].filter(Boolean).join(" · ")}
        </div>} />
      )}
    </div>
  );
};

const MetaRow = ({ k, v, testid }) => (
  <div data-testid={testid} className="flex gap-3 items-start text-xs">
    <div className="text-[#7A7A7A] shrink-0 min-w-[140px]">{k}</div>
    <div className="min-w-0 flex-1">{v}</div>
  </div>
);

const ProjectBriefBlock = ({ brief }) => {
  const rows = [
    ["Business Name", brief.business_name],
    ["Industry", brief.industry],
    ["Deadline", brief.deadline],
  ].filter(([, v]) => v);
  const refImages = Array.isArray(brief.reference_images) ? brief.reference_images : [];
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const absUrl = (u) => (u && u.startsWith("/") ? `${backendUrl}${u}` : u);
  const isImage = (u) => /\.(png|jpe?g|webp|gif)$/i.test(u || "");
  const hasContact = brief.contact_name || brief.email || brief.phone;
  return (
    <div data-testid="admin-project-brief" className="mt-3 pt-3 border-t border-[#262626]">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF6600] flex items-center gap-1">
        <FileText className="w-3 h-3" /> Project Brief
      </div>
      {hasContact && (
        <div data-testid="admin-brief-contact" className="mt-2 rounded-lg border border-[#FF6600]/30 bg-[#FF6600]/[0.04] p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF6600] mb-1.5">Contact</div>
          <dl className="grid sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
            {brief.contact_name && (
              <div className="flex gap-2 min-w-0">
                <dt className="text-[#7A7A7A] shrink-0">Name:</dt>
                <dd data-testid="admin-brief-contact-name" className="text-white truncate">{brief.contact_name}</dd>
              </div>
            )}
            {brief.email && (
              <div className="flex gap-2 min-w-0">
                <dt className="text-[#7A7A7A] shrink-0">Email:</dt>
                <dd className="truncate">
                  <a data-testid="admin-brief-contact-email" href={`mailto:${brief.email}`} className="text-[#FF6600] hover:underline">{brief.email}</a>
                </dd>
              </div>
            )}
            {brief.phone && (
              <div className="flex gap-2 min-w-0">
                <dt className="text-[#7A7A7A] shrink-0">Phone:</dt>
                <dd className="truncate">
                  <a data-testid="admin-brief-contact-phone" href={`tel:${brief.phone.replace(/\s+/g, "")}`} className="text-[#FF6600] hover:underline">{brief.phone}</a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}
      <dl className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="text-[#7A7A7A] shrink-0">{k}:</dt>
            <dd className="text-white">{v}</dd>
          </div>
        ))}
      </dl>
      {brief.description && (
        <div className="mt-3">
          <div className="text-[10px] text-[#7A7A7A] uppercase tracking-widest">Description</div>
          <div className="mt-1 text-xs whitespace-pre-wrap text-[#D1D1D1]">{brief.description}</div>
        </div>
      )}
      {brief.reference_links && (
        <div className="mt-3">
          <div className="text-[10px] text-[#7A7A7A] uppercase tracking-widest">Reference Links / Notes</div>
          <div className="mt-1 text-xs whitespace-pre-wrap text-[#D1D1D1]">{brief.reference_links}</div>
        </div>
      )}
      {refImages.length > 0 && (
        <div className="mt-3" data-testid="admin-brief-references">
          <div className="text-[10px] text-[#7A7A7A] uppercase tracking-widest">Reference Images / Sketches</div>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {refImages.map((u) => {
              const href = absUrl(u);
              return isImage(u) ? (
                <a key={u} href={href} target="_blank" rel="noopener noreferrer" className="block rounded-lg overflow-hidden border border-[#262626] hover:border-[#FF6600] transition-colors bg-[#0F0F0F] aspect-square">
                  <img src={href} alt="reference" className="w-full h-full object-cover" />
                </a>
              ) : (
                <a key={u} href={href} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-[#262626] hover:border-[#FF6600] transition-colors bg-[#0F0F0F] aspect-square grid place-items-center text-[10px] text-[#D1D1D1] gap-1 p-2">
                  <FileText className="w-6 h-6 text-[#FF6600]" />
                  <span className="truncate max-w-full">PDF</span>
                </a>
              );
            })}
          </div>
        </div>
      )}
      {brief.website_details && <WebsiteDetailsAdminBlock wd={brief.website_details} />}
    </div>
  );
};

// Website Design Pass 3C — human-readable Website Details block for
// Admin Orders. Renders the structured production intake captured by
// the Website Brief section. Historical orders (before Pass 3C)
// simply won't have `website_details` — this block returns null.
const WD_LABELS = {
  project_type: { new: "New website", redesign: "Redesign existing" },
  domain_status: { own: "Client owns domain", need_help: "Needs help / new domain" },
  content_readiness: { yes: "Ready", partial: "Partially ready", no: "Not ready yet" },
  languages: { one: "1 language (included)", many: "More than 1 (estimate)" },
  primary_goal: {
    leads: "Generate leads / contact requests",
    info: "Present services / business info",
    bookings: "Accept bookings",
    payments: "Accept simple payments",
    sell: "Sell products online",
    other: "Other",
  },
  brand_assets: { yes: "Ready to share", partial: "Partially ready", no: "Needs help creating" },
};
const WebsiteDetailsAdminBlock = ({ wd }) => {
  if (!wd || typeof wd !== "object") return null;
  const label = (k) => (WD_LABELS[k] && WD_LABELS[k][wd[k]]) || wd[k] || "—";
  const rows = [
    ["Project type", label("project_type")],
    wd.project_type === "redesign" ? ["Current URL", wd.current_url || "—"] : null,
    ["Domain", label("domain_status")],
    wd.domain_status === "own" ? ["Domain / address", wd.domain_url || "—"] : null,
    ["Content readiness", label("content_readiness")],
    ["Languages", label("languages")],
    wd.languages === "many" ? ["Language detail", wd.languages_detail || "—"] : null,
    ["Primary goal", label("primary_goal")],
    ["Brand assets", label("brand_assets")],
  ].filter(Boolean);
  return (
    <div
      data-testid="admin-website-details"
      className="mt-3 rounded-lg border border-[#FF6600]/30 bg-[#FF6600]/[0.04] p-3"
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF6600] mb-2">
        Website Details
      </div>
      <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-2 min-w-0">
            <dt className="text-[#7A7A7A] shrink-0">{k}:</dt>
            <dd className="text-white truncate">{v}</dd>
          </div>
        ))}
      </dl>
      {wd.expected_pages && (
        <div className="mt-2">
          <div className="text-[10px] text-[#7A7A7A] uppercase tracking-widest">Expected pages</div>
          <div className="mt-1 text-xs whitespace-pre-wrap text-[#D1D1D1]">{wd.expected_pages}</div>
        </div>
      )}
    </div>
  );
};
